import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Pod } from '../../types';
import apiService from '../../services/api';

export interface PodsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface PodsState {
  pods: Pod[];
  /** Server pagination of the last `fetchPods` (total = whole filtered set). */
  pagination: PodsPagination | null;
  currentPod: Pod | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: PodsState = {
  pods: [],
  pagination: null,
  currentPod: null,
  isLoading: false,
  error: null,
};

export type FetchPodsParams = Parameters<typeof apiService.getPods>[0];

// Async thunks
export const fetchPods = createAsyncThunk(
  'pods/fetchPods',
  async (params: FetchPodsParams | undefined, { rejectWithValue }) => {
    try {
      const response = await apiService.getPods(params);
      return {
        pods: (response.results || []) as Pod[],
        pagination: (response.pagination ?? null) as PodsPagination | null,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pods');
    }
  }
);

export const fetchPod = createAsyncThunk(
  'pods/fetchPod',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiService.getPod(id);
      return (response as any).result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pod');
    }
  }
);

export const createPod = createAsyncThunk(
  'pods/createPod',
  async (podData: any, { rejectWithValue }) => {
    try {
      const response = await apiService.createPod(podData);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create pod');
    }
  }
);

export const updatePod = createAsyncThunk(
  'pods/updatePod',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await apiService.updatePod(id, data);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update pod');
    }
  }
);

export const deletePod = createAsyncThunk(
  'pods/deletePod',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiService.deletePod(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete pod');
    }
  }
);

const podsSlice = createSlice({
  name: 'pods',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPod: (state, action: PayloadAction<Pod | null>) => {
      state.currentPod = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch pods
      .addCase(fetchPods.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pods = action.payload.pods;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchPods.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single pod
      .addCase(fetchPod.fulfilled, (state, action) => {
        state.currentPod = action.payload as Pod;
      })
      // Create pod
      .addCase(createPod.fulfilled, (state, action) => {
        state.pods.push(action.payload as Pod);
      })
      // Update pod
      .addCase(updatePod.fulfilled, (state, action) => {
        const updatedPod = action.payload as Pod;
        const index = state.pods.findIndex(pod => pod.id === updatedPod.id);
        if (index !== -1) {
          state.pods[index] = updatedPod;
        }
        if (state.currentPod?.id === updatedPod.id) {
          state.currentPod = updatedPod;
        }
      })
      // Delete pod
      .addCase(deletePod.fulfilled, (state, action) => {
        state.pods = state.pods.filter(pod => pod.id !== action.payload);
        if (state.currentPod?.id === action.payload) {
          state.currentPod = null;
        }
      });
  },
});

export const { clearError, setCurrentPod } = podsSlice.actions;
export default podsSlice.reducer;
